import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import { triggerExcelDownload } from '@/utils/excelDownload';
import type {
  VDM,
  ListVDMsInput,
  ListVDMsResponse,
  GetVDMResponse,
  CreateVDMInput,
  CreateVDMResponse,
  CreateBatchVDMsInput,
  CreateBatchVDMsResponse,
  UpdateVDMInput,
  UpdateVDMResponse,
  SendVDMInput,
  SendVDMResponse,
  RefreshAS400Input,
  RefreshAS400Response,
  RefreshAllAS400Response,
  AddVDMCommentInput,
  AddVDMCommentResponse,
  DeleteVDMInput,
  DeleteVDMResponse,
  DownloadVDMsExcelResponse,
} from '@erp/rpc';

export function useVDMCompanies() {
  const query = useQuery(
    orpc.shipAndDebit.vdms.listCompanies.queryOptions({ input: {} }),
  );

  return {
    ...query,
    companies: query.data?.companies ?? [],
  };
}

export function useCompanyHierarchy() {
  const query = useQuery(
    orpc.shipAndDebit.vdms.getCompanyHierarchy.queryOptions({ input: {} }),
  );

  return {
    ...query,
    companies: query.data?.companies ?? [],
  };
}

export function useVDMsList(filters: ListVDMsInput = {}, enabled = true) {
  const query = useQuery({
    ...orpc.shipAndDebit.vdms.list.queryOptions({
      input: filters,
    }),
    enabled,
  });

  const vdms = query.data?.vdms ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    vdms,
    pagination,
  } satisfies typeof query & {
    data?: ListVDMsResponse;
    vdms: VDM[];
    pagination: ListVDMsResponse['pagination'] | undefined;
  };
}

/**
 * Download the VDMs matching a set of list filters as an Excel workbook (every
 * match, up to the server's cap). The browser download starts on success;
 * `onSuccess` sees the response so the caller can flag a truncated export.
 */
export function useDownloadVDMsExcel(options?: {
  onSuccess?: (data: DownloadVDMsExcelResponse) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    ...orpc.shipAndDebit.vdms.downloadExcel.mutationOptions(),
    onSuccess: (data) => {
      triggerExcelDownload(data.excel, data.filename);
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useVDM(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.shipAndDebit.vdms.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    vdm: query.data ?? null,
  } satisfies typeof query & {
    data?: GetVDMResponse;
    vdm: GetVDMResponse | null;
  };
}

export function useCreateVDM() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<CreateVDMResponse, Error, CreateVDMInput>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.create(input);
    },
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'VDM created successfully',
        color: 'success',
      });

      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to create VDM',
        color: 'error',
      });
    },
  });
}

export function useCreateBatchVDMs() {
  const queryClient = useQueryClient();

  return useMutation<CreateBatchVDMsResponse, Error, CreateBatchVDMsInput>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.createBatch(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
      });
    },
  });
}

export function useUpdateVDM() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<UpdateVDMResponse, Error, UpdateVDMInput>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.update(input);
    },
    onSuccess: (data) => {
      showToast({
        title: 'Success',
        message: 'VDM updated successfully',
        color: 'success',
      });

      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.get.queryKey({
          input: { id: data.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update VDM',
        color: 'error',
      });
    },
  });
}

export function useSendVDM() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<SendVDMResponse, Error, SendVDMInput>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.send(input);
    },
    onSuccess: (data) => {
      showToast({
        title: 'Success',
        message: 'VDM sent successfully',
        color: 'success',
      });

      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.get.queryKey({
          input: { id: data.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to send VDM',
        color: 'error',
      });
    },
  });
}

export function useRetryVDMCreation() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<SendVDMResponse, Error, UpdateVDMInput>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.retryCreation(input);
    },
    onSuccess: (data) => {
      showToast({
        title: 'Success',
        message: 'VDM creation retried — sent to AS400',
        color: 'success',
      });

      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.get.queryKey({
          input: { id: data.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.listActivities.queryKey({
          input: { vdmId: data.id },
        }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to retry VDM creation',
        color: 'error',
      });
    },
  });
}

export function useRefreshAS400() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<RefreshAS400Response, Error, RefreshAS400Input>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.refreshAS400(input);
    },
    onSuccess: (data, variables) => {
      if (!data.found) {
        showToast({
          title: 'Info',
          message: data.message,
          color: 'info',
        });
      } else if (data.vdm?.error) {
        showToast({
          title: 'AS400 Errors',
          message: data.message,
          color: 'error',
        });
      } else {
        showToast({
          title: 'Success',
          message: data.message,
          color: 'success',
        });
      }

      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.get.queryKey({
          input: { id: variables.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.listActivities.queryKey({
          input: { vdmId: variables.id },
        }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to refresh AS400 status',
        color: 'error',
      });
    },
  });
}

export function useRefreshAllAS400() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<RefreshAllAS400Response, Error, void>({
    mutationFn: async () => {
      return await rpcClient.shipAndDebit.vdms.refreshAllAS400({});
    },
    onSuccess: (data) => {
      if (data.updatedCount > 0) {
        showToast({
          title: 'AS400 Refreshed',
          message: data.message,
          color: 'success',
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
        });
      }
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to refresh AS400 status',
        color: 'error',
      });
    },
  });
}

export function useVDMActivities(vdmId: string | undefined) {
  const query = useQuery(
    orpc.shipAndDebit.vdms.listActivities.queryOptions({
      input: vdmId ? { vdmId } : skipToken,
    }),
  );

  return {
    ...query,
    activities: query.data?.activities ?? [],
    pagination: query.data?.pagination,
  };
}

export function useDeleteVDM() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<DeleteVDMResponse, Error, DeleteVDMInput>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.delete(input);
    },
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'VDM deleted successfully',
        color: 'success',
      });

      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.list.queryKey({ input: {} }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete VDM',
        color: 'error',
      });
    },
  });
}

export function useAddVDMComment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<AddVDMCommentResponse, Error, AddVDMCommentInput>({
    mutationFn: async (input) => {
      return await rpcClient.shipAndDebit.vdms.addComment(input);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.listActivities.queryKey({
          input: { vdmId: variables.vdmId },
        }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to add comment',
        color: 'error',
      });
    },
  });
}

export function useVDMLinkedSubmissions(vdmId: string | undefined) {
  const query = useQuery(
    orpc.shipAndDebit.vdms.getLinkedSubmissions.queryOptions({
      input: vdmId ? { vdmId } : skipToken,
    }),
  );

  return {
    ...query,
    submissions: query.data?.submissions ?? [],
  };
}

export function useLinkSubmissionFromVDM() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: { vdmId: string; submissionId: string }) => {
      return rpcClient.shipAndDebit.vdms.linkSubmission(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.list.queryKey({
          input: {},
        }),
      });
      showToast({
        message: 'Submission linked to VDM',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to link submission',
        color: 'error',
      });
    },
  });
}

export function useUnlinkVDMSubmission() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: { vdmId: string; submissionId: string }) => {
      return rpcClient.shipAndDebit.vdms.unlinkSubmission(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.list.queryKey({
          input: {},
        }),
      });
      showToast({
        message: 'Submission unlinked from VDM',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to unlink submission',
        color: 'error',
      });
    },
  });
}

/**
 * Link specific 844 transactions (sd_e844d ids) to a VDM, per line. The backend
 * rejects transactions that aren't in the VDM's tax province.
 */
export function useLinkTransactionsToVdm() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: { vdmId: string; transactionIds: string[] }) => {
      return rpcClient.shipAndDebit.vdms.linkTransactions(input);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.key(),
      });
      showToast({
        message: `Linked ${data.linkedCount} transaction(s) to VDM`,
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to link transactions to VDM',
        color: 'error',
      });
    },
  });
}

/** Unlink specific 844 transactions (sd_e844d ids) from their VDM. */
export function useUnlinkTransactions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: { transactionIds: string[] }) => {
      return rpcClient.shipAndDebit.vdms.unlinkTransactions(input);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.key(),
      });
      showToast({
        message: `Unlinked ${data.unlinkedCount} transaction(s) from VDM`,
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to unlink transactions',
        color: 'error',
      });
    },
  });
}
